import { useState, useRef, useMemo, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Cake, Gift, Cookie, Croissant, Coffee, Star } from 'lucide-react';
import { menuCategories, allMenuItems, formatPrice, businessInfo } from '../data/menuData';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

// Animation variants defined outside component
const sectionHeaderVariants = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 }
};

const tabsVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 }
};

const gridVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 }
};

const itemVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 }
};

const transitionConfig = { duration: 0.6 };
const delayedTransition = { duration: 0.6, delay: 0.2 };
const gridTransition = { duration: 0.4 };

// Category Icon Component
const CategoryIcon = ({ iconName }) => {
  const icons = {
    cake: Cake,
    gift: Gift,
    cookie: Cookie,
    croissant: Croissant,
    coffee: Coffee,
    star: Star
  };
  const Icon = icons[iconName] || Cake;
  return <Icon size={18} />;
};

// Filter function defined outside component
const filterItems = (cat) => allMenuItems.filter(item => item.category === cat);

// Menu Item Card Component
const MenuItemCard = ({ item, index, onOrder }) => (
  <motion.div
    key={item.name}
    variants={itemVariants}
    initial="initial"
    animate="animate"
    transition={{ delay: index * 0.05 }}
    className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-bakery-border/50 dark:border-gray-700 menu-card"
    data-testid={`menu-item-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
  >
    <div className="flex justify-between items-start mb-3">
      <h3 className="font-heading text-xl font-semibold text-bakery-text dark:text-bakery-bg">
        {item.name}
      </h3>
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
        item.custom 
          ? 'bg-bakery-accent dark:bg-gray-700 text-bakery-text dark:text-bakery-bg' 
          : 'price-badge text-white'
      }`}>
        {formatPrice(item.price)}
      </span>
    </div>
    <p className="text-bakery-text/70 dark:text-bakery-bg/70 text-sm mb-4">{item.description}</p>
    <button
      onClick={() => onOrder(item)}
      className="w-full flex items-center justify-center gap-2 border-2 border-bakery-text dark:border-bakery-bg text-bakery-text dark:text-bakery-bg py-3 rounded-full font-medium hover:bg-bakery-text dark:hover:bg-bakery-bg hover:text-bakery-bg dark:hover:text-bakery-text transition-colors duration-300"
      data-testid={`order-btn-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <Phone size={16} />
      Order Now
    </button>
  </motion.div>
);

// Order Modal Component
const OrderModal = ({ isOpen, onClose, item }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="bg-bakery-bg dark:bg-gray-800 border-bakery-border dark:border-gray-700" data-testid="order-modal">
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl text-bakery-text dark:text-bakery-bg">
          Order {item?.name}
        </DialogTitle>
        <DialogDescription className="text-bakery-text/70 dark:text-bakery-bg/70">
          {item?.custom 
            ? 'Contact us to discuss your personalized order'
            : `Price: ${formatPrice(item?.price)}`
          }
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 mt-4">
        <p className="text-bakery-text dark:text-bakery-bg">
          Ready to order? Contact us through:
        </p>
        <a
          href={`tel:${businessInfo.phone}`}
          className="flex items-center justify-center gap-2 bg-bakery-text dark:bg-bakery-primary text-bakery-bg py-4 rounded-full font-medium btn-lift w-full"
          data-testid="modal-call-btn"
        >
          <Phone size={20} />
          Call {businessInfo.phone}
        </a>
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-bakery-border dark:border-gray-600" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-bakery-bg dark:bg-gray-800 px-2 text-bakery-text/60 dark:text-bakery-bg/60">or</span>
          </div>
        </div>
        <div className="text-center p-4 bg-bakery-muted dark:bg-gray-700 rounded-xl">
          <p className="text-bakery-text dark:text-bakery-bg font-medium">
            Order via <span className="text-bakery-primary font-bold">Msosi App</span>
          </p>
          <p className="text-sm text-bakery-text/70 dark:text-bakery-bg/70 mt-1">
            Download the app for easy delivery ordering
          </p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

const MenuSection = () => {
  const [activeCategory, setActiveCategory] = useState('cakes');
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleOrder = useCallback((item) => {
    setSelectedItem(item);
    setIsOrderModalOpen(true);
  }, []);

  const handleCloseModal = useCallback((open) => {
    setIsOrderModalOpen(open);
  }, []);

  const currentItems = useMemo(() => filterItems(activeCategory), [activeCategory]);

  const animateProps = useMemo(() => 
    isInView ? sectionHeaderVariants.animate : sectionHeaderVariants.initial
  , [isInView]);

  return (
    <section id="menu" className="py-20 md:py-32 bg-bakery-bg dark:bg-gray-900" data-testid="menu-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={sectionHeaderVariants.initial}
          animate={animateProps}
          transition={transitionConfig}
          className="text-center mb-16"
        >
          <span className="font-accent text-2xl text-bakery-primary">Delicious Treats</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-bakery-text dark:text-bakery-bg mt-2">
            Our <span className="text-bakery-primary">Menu</span>
          </h2>
          <p className="text-bakery-text/70 dark:text-bakery-bg/70 mt-4 max-w-2xl mx-auto">
            Explore our wide selection of freshly baked goods, from custom cakes to savory bites
          </p>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={tabsVariants.initial}
          animate={animateProps}
          transition={delayedTransition}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {menuCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-bakery-text dark:bg-bakery-primary text-bakery-bg shadow-lg'
                  : 'bg-white dark:bg-gray-800 text-bakery-text dark:text-bakery-bg hover:bg-bakery-muted dark:hover:bg-gray-700'
              }`}
              data-testid={`menu-tab-${category.id}`}
            >
              <CategoryIcon iconName={category.icon} />
              {category.name}
            </button>
          ))}
        </motion.div>

        {/* Menu Items Grid */}
        <motion.div
          key={activeCategory}
          initial={gridVariants.initial}
          animate={gridVariants.animate}
          transition={gridTransition}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {currentItems.map((item, index) => (
            <MenuItemCard 
              key={item.name} 
              item={item} 
              index={index} 
              onOrder={handleOrder} 
            />
          ))}
        </motion.div>

        <OrderModal 
          isOpen={isOrderModalOpen} 
          onClose={handleCloseModal} 
          item={selectedItem} 
        />
      </div>
    </section>
  );
};

export default MenuSection;
